import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage, translations } from '../context/LanguageContext';

const { FiPlus, FiCheck, FiTrash2, FiClock, FiTarget } = FiIcons;

const TaskSection = ({ tasks, onAddTask, onToggleTask, onDeleteTask }) => {
  const { language } = useLanguage();
  const t = translations[language];

  const [newTask, setNewTask] = useState({
    title: '',
    priority: 'medium',
    category: 'personal'
  });
  const [isAdding, setIsAdding] = useState(false);

  const priorities = {
    high: {
      color: 'bg-red-500',
      text: language === 'de' ? t.highPriority : 'High'
    },
    medium: {
      color: 'bg-yellow-500',
      text: language === 'de' ? t.mediumPriority : 'Medium'
    },
    low: {
      color: 'bg-green-500',
      text: language === 'de' ? t.lowPriority : 'Low'
    }
  };

  const categories = {
    personal: {
      color: 'bg-purple-500',
      text: language === 'de' ? t.personal : 'Personal'
    },
    work: {
      color: 'bg-blue-500',
      text: language === 'de' ? t.work : 'Work'
    },
    learning: {
      color: 'bg-indigo-500',
      text: language === 'de' ? t.learning : 'Learning'
    },
    health: {
      color: 'bg-green-500',
      text: language === 'de' ? t.health : 'Health'
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newTask.title.trim()) {
      onAddTask(newTask);
      setNewTask({
        title: '',
        priority: 'medium',
        category: 'personal'
      });
      setIsAdding(false);
    }
  };

  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={FiTarget} className="text-2xl text-purple-300 mr-3" />
          <h2 className="text-2xl font-bold text-white">{t.eveningTasks}</h2>
        </div>
        <div className="text-purple-200 text-sm">
          {completedTasks}/{totalTasks} {t.completed}
        </div>
      </div>

      {totalTasks > 0 && (
        <div className="mb-6">
          <div className="bg-white/5 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-300"
              style={{
                width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%`
              }}
            />
          </div>
        </div>
      )}

      <AnimatePresence>
        {tasks.map((task) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className={`mb-3 p-4 rounded-xl border transition-all duration-200 ${
              task.completed
                ? 'bg-white/5 border-green-500/30 opacity-75'
                : 'bg-white/10 border-white/20 hover:border-purple-400/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1">
                <button
                  onClick={() => onToggleTask(task.id)}
                  className={`mr-3 p-2 rounded-full transition-all duration-200 ${
                    task.completed
                      ? 'bg-green-500 text-white'
                      : 'bg-white/10 text-purple-300 hover:bg-green-500/20'
                  }`}
                >
                  <SafeIcon icon={FiCheck} className="text-sm" />
                </button>
                <div className="flex-1">
                  <h3
                    className={`font-medium ${
                      task.completed ? 'text-white/60 line-through' : 'text-white'
                    }`}
                  >
                    {task.title}
                  </h3>
                  <div className="flex items-center mt-1 space-x-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs text-white ${
                        priorities[task.priority].color
                      }`}
                    >
                      {priorities[task.priority].text}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs text-white ${
                        categories[task.category].color
                      }`}
                    >
                      {categories[task.category].text}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onDeleteTask(task.id)}
                className="ml-3 p-2 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-all duration-200"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {isAdding ? (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="mt-4 p-4 bg-white/5 rounded-xl border border-white/20"
        >
          <input
            type="text"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            placeholder={t.taskPlaceholder}
            className="w-full bg-white/10 text-white placeholder-white/60 px-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:border-purple-400 mb-3"
            autoFocus
          />
          <div className="flex space-x-3 mb-3">
            <select
              value={newTask.priority}
              onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
              className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-purple-400"
            >
              <option value="high">{t.highPriority}</option>
              <option value="medium">{t.mediumPriority}</option>
              <option value="low">{t.lowPriority}</option>
            </select>
            <select
              value={newTask.category}
              onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
              className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-purple-400"
            >
              <option value="personal">{t.personal}</option>
              <option value="work">{t.work}</option>
              <option value="learning">{t.learning}</option>
              <option value="health">{t.health}</option>
            </select>
          </div>
          <div className="flex space-x-2">
            <button
              type="submit"
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              {t.add}
            </button>
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              {t.cancel}
            </button>
          </div>
        </motion.form>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full mt-4 p-4 bg-white/5 border-2 border-dashed border-white/30 rounded-xl text-white/70 hover:text-white hover:border-purple-400/50 transition-all duration-200 flex items-center justify-center"
        >
          <SafeIcon icon={FiPlus} className="text-xl mr-2" />
          {t.addTask}
        </button>
      )}
    </motion.div>
  );
};

export default TaskSection;